// PropertySearchResults.js
import React, { useState, useEffect } from 'react';
import { useParams, useOutletContext, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Link } from 'react-router-dom'; // Import Link for navigation
import './TaxSearchResults.css';

const apiClient = axios.create({
  baseURL: process.env.REACT_APP_PROXY_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

const TaxSearchResults = () => {
  const { customerId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { dataClient } = useOutletContext(); // Access dataClient

  const formData = location.state?.formData;

  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const yearshistory = formData?.yearshistory;
  const taxyear = formData?.taxyear;
  const payoffDate = formData?.payoffDate ?? null;

  const hasValidCadastralGeo = (legal) => {
    const geo = legal?.legalPropertyGeo ?? '';
    // Valid if it contains at least one digit (rejects "  -  - -  -  -" and blanks)
    return /\d/.test(geo);
  };

  useEffect(() => {
    if (!formData) {
      // If formData is not available, redirect back to the search form
      navigate(`/customer/${customerId}`);
      return;
    }

    const fetchResults = async () => {
      try {
        const apiUrl = `/api/propertySearch/${customerId}`;
        const response = await apiClient.post(apiUrl, formData);
        setResults(response.data);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching property search results:', error);
        setError(error);
        setLoading(false);
      }
    };

    fetchResults();
  }, [customerId, formData, navigate]);

  const formatName = (name1, name2) => {
    if (name2 && name2.trim() !== '') {
      return `${name1} ${name2}`;
    }
    return name1;
  };

  const formatAddress = (address1, address2, city, state, zip) => {
    let fullAddress = address1;
    if (address2 && address2.trim() !== '') {
      fullAddress += `, ${address2}`;
    }
    fullAddress += `, ${city}, ${state} ${zip}`;
    return fullAddress;
  };
  
  if (loading) {
    return <div>Loading search results...</div>;
  }

  if (error) {
    return <div>Error fetching results: {error.message}</div>;
  }

  if (!dataClient) {
    return <div>Loading client data...</div>;
  }

  return (
    <div className="property-search-results">
      <h1><strong>Search Results for {dataClient.name}</strong></h1>
      {results.length > 0 ? (
        <ul className="results-list">
          {results.map((result) => (
            <li key={result.propertyTaxID} className="result-item">
              {/* Left Column: Property Details */}
              <div className="left-column">
                <div><strong>Tax ID:</strong> {result.propertyTaxID}</div>
                <div><strong>Owner Name:</strong> {formatName(result.propertyOwnerName1, result.propertyOwnerName2)}</div>
                <div><strong>Address:</strong> {formatAddress(
                  result.propertyOwnerAddress1,
                  result.propertyOwnerAddress2,
                  result.propertyOwnerCity,
                  result.propertyOwnerState,
                  result.propertyOwnerZip
                )}</div>
                {/* Add more fields as necessary */}
              </div>

              {/* Right Column: Detail Page Link */}
              <div className="right-column">
                <Link to={`/customer/${customerId}/taxStatus/${result.propertyTaxID}/${taxyear}`} className="detail-link">
                  Status
                </Link>
                <Link to={`/customer/${customerId}/history/${result.propertyTaxID}/${taxyear}/${yearshistory}`} state={{ payoffDate, fromSection: 'tax' }} className="detail-link">
                  History
                </Link>
                <Link to={`/customer/${customerId}/propertyDetail/${result.propertyTaxID}/${taxyear}`} className="detail-link">
                  Property
                </Link>
                {hasValidCadastralGeo(result.propertyLegal) ? (
                  <a
                    href={`https://svc.mt.gov/msl/cadastral/?page=PropertyDetails&geocode=${dataClient.county_number}${result.propertyLegal.legalPropertyGeo.replace(/-/g, '')}&taxYear=20${taxyear}&yearshistory=${yearshistory}`}
                    className="detail-link"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Cadastral
                  </a>
                ) : (
                  <a className="detail-link disabled-link" role="link" aria-disabled="true" tabIndex={-1}>
                    Cadastral
                  </a>
                )}
                <Link to={`/customer/${customerId}/taxDetail/${result.propertyTaxID}/${taxyear}`} className="detail-link">
                  Detail
                </Link>
                
                <Link to={`/customer/${customerId}/billImage/images/${customerId}/${result.propertyTaxID}/${taxyear}`} className="detail-link">
                  Bill Image
                </Link>

                <Link to={`/customer/${customerId}/TaxRoll/${result.propertyTaxID}/${taxyear}`} className="detail-link">
                  Tax Roll
                </Link>
                
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <p>No results found.</p>
      )}
    </div>
  );
};

export default TaxSearchResults;
